// src/hooks/useCalendarStatus.js
import { useQuery } from 'react-query';
import { fetchUtils } from 'react-admin';
import { createTenantQueryKey, extractTenantFromHostname } from '../utils/tenant';

// Helper function for HTTP requests with tenant awareness
const httpClient = (url, options = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }
  const token = localStorage.getItem('token');
  options.headers.set('Authorization', `Bearer ${token}`);
  
  // Get API URL with proper tenant
  const tenant = extractTenantFromHostname();
  
  // Determine base API URL (read from runtime config)
  const nodeEnv = window.APP_CONFIG?.NODE_ENV || 'production';

  let apiUrl;
  if (nodeEnv === 'development') {
    apiUrl = `http://${tenant}.api.wygc.eu:8080/api`;
  } else {
    apiUrl = `https://${tenant}.api.wygc.eu/api`;
  }
  
  // Ensure the URL is properly formed
  const fullUrl = url.startsWith('http') ? url : `${apiUrl}/${url.replace(/^\//, '')}`;
  
  console.log(`Calendar status check to: ${fullUrl}`);
  
  return fetchUtils.fetchJson(fullUrl, options);
};

/**
 * Custom hook to check if a calendar URL is accessible
 * @param {string} teamId - The team ID
 * @param {string} calendarId - The calendar ID to check
 * @param {Object} options - Additional React Query options
 * @returns {Object} Query result with status data
 */
export const useCalendarStatus = (teamId, calendarId, options = {}) => {
  const tenant = extractTenantFromHostname();
  
  return useQuery(
    createTenantQueryKey(['calendarStatus', teamId, calendarId, tenant]),
    async () => {
      if (!teamId || !calendarId) {
        throw new Error('Team ID and Calendar ID are required');
      }

      const checkUrl = `teams/${teamId}/calendars/${calendarId}/check`;
      try {
        const { json } = await httpClient(checkUrl);
        return { 
          status: 'success', 
          message: json.message || 'URL is accessible', 
          code: json.status || 200 
        };
      } catch (error) {
        // Handle different error types
        if (error.status === 404) {
          return { 
            status: 'error', 
            message: 'Calendar not found', 
            code: 404 
          };
        } else if (error.status === 500) {
          return { 
            status: 'error', 
            message: error.body?.error || 'URL is not reachable', 
            code: 500 
          };
        } else {
          return { 
            status: 'error', 
            message: error.body?.error || 'Error checking URL', 
            code: error.status || 0 
          };
        }
      }
    },
    {
      enabled: !!teamId && !!calendarId,
      staleTime: 30 * 60 * 1000,      // 30 minutes
      cacheTime: 60 * 60 * 1000,      // 60 minutes
      refetchOnWindowFocus: false,    // Don't refetch when window regains focus
      retry: false,                   // Don't retry failed requests automatically
      ...options                      // Allow overriding default options
    }
  );
};